import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Avatar,
  IconButton,
  LinearProgress,
  Chip,
  Menu,
  MenuItem,
  Tooltip,
  Popover,
  List,
  ListItem,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  MoreVert as MoreIcon,
  Male as MaleIcon,
  Female as FemaleIcon,
  Share as ShareIcon,
  Print as PrintIcon,
  AccountTree as TreeIcon,  // ✅ NEW: Tree navigation icon
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { differenceInYears } from 'date-fns';
import type { Person, LocationFormData } from '../../../../types';
import { MetadataDisplay } from '../components/MetadataDisplay';
import { ProfileValidationService } from '../../../../services/profileValidationService';
import type { ProfileCompletenessConfig, CompletenessField } from '../../../../types/admin';
import ClaimProfileButton from '../../../../components/profiles/ClaimProfileButton';
import UserBadge from '../../../../components/profiles/UserBadge';

interface PersonHeroProps {
  person: Person;
  completeness: number;
  onEdit: () => void;
  // ✅ Metadata display (from critical fixes)
  showMetadata?: boolean;
  metadataFormat?: 'short' | 'long';
}

export const PersonHero: React.FC<PersonHeroProps> = ({
  person,
  completeness,
  onEdit,
  showMetadata = false,
  metadataFormat = 'short'
}) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [infoAnchorEl, setInfoAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [config, setConfig] = useState<ProfileCompletenessConfig | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(false);

  // Load config when info popover opens
  useEffect(() => {
    if (infoAnchorEl && !config) {
      loadConfig();
    }
  }, [infoAnchorEl, config]);

  const loadConfig = async () => {
    try {
      setLoadingConfig(true);
      const data = await ProfileValidationService.getConfig();
      setConfig(data);
    } catch (err) {
      console.error('Failed to load completeness config:', err);
    } finally {
      setLoadingConfig(false);
    }
  };

  /**
   * Check if a field is applicable to this person
   */
  const isFieldApplicable = (field: CompletenessField): boolean => {
    // Death-related fields only apply to deceased people
    if (field === 'deathDate' || field === 'deathPlace') {
      return person.isDeceased;
    }
    return true;
  };

  /**
   * Check if a field is filled for the person
   * NOTE: Only call this for applicable fields (filtered by isFieldApplicable)
   */
  const isFieldFilled = (field: CompletenessField): boolean => {
    switch (field) {
      case 'deathDate':
        // Only called for deceased people (filtered by isFieldApplicable)
        return !!person.deathDate;

      case 'deathPlace':
        // Only called for deceased people (filtered by isFieldApplicable)
        const deathLocation = person.deathPlace as LocationFormData | undefined;
        return !!(deathLocation?.city?.trim() || deathLocation?.state?.trim() || deathLocation?.country?.trim());

      case 'birthPlace':
      case 'currentLocation':
        const location = person[field] as LocationFormData | undefined;
        return !!(location?.city?.trim() || location?.state?.trim() || location?.country?.trim());

      case 'nicknames':
        return !!(person.nicknames && person.nicknames.length > 0 && person.nicknames.some(n => n.trim()));

      default:
        const value = person[field as keyof Person];
        if (typeof value === 'string') {
          return !!value.trim();
        }
        return value !== undefined && value !== null;
    }
  };

  // =========================================================================
  // Computed Values
  // =========================================================================

  /**
   * Calculate age (living or at death)
   */
  const age = useMemo(() => {
    if (!person.birthDate) return null;

    if (person.isDeceased && person.deathDate) {
      return differenceInYears(
        new Date(person.deathDate),
        new Date(person.birthDate)
      );
    }

    return differenceInYears(new Date(), new Date(person.birthDate));
  }, [person.birthDate, person.deathDate, person.isDeceased]);

  /**
   * Format life span display
   * Examples: "1945 - 2023" or "b. 1945"
   */
  const lifeSpan = useMemo(() => {
    const birthYear = person.birthDate
      ? new Date(person.birthDate).getFullYear()
      : '?';

    if (person.isDeceased) {
      const deathYear = person.deathDate
        ? new Date(person.deathDate).getFullYear()
        : '?';
      return `${birthYear} - ${deathYear}`;
    }

    return `b. ${birthYear}`;
  }, [person.birthDate, person.deathDate, person.isDeceased]);

  /**
   * Avatar initials fallback
   */
  const initials = useMemo(() => {
    return `${person.firstName.charAt(0)}${person.lastName.charAt(0)}`.toUpperCase();
  }, [person.firstName, person.lastName]);

  /**
   * Completeness bar color
   * - Green: ≥80%
   * - Yellow: 50-79%
   * - Red: <50%
   */
  const completenessColor = useMemo(() => {
    if (completeness >= 80) return 'success';
    if (completeness >= 50) return 'warning';
    return 'error';
  }, [completeness]);

  // =========================================================================
  // Event Handlers
  // =========================================================================

  const handleMoreClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMoreClose = () => {
    setAnchorEl(null);
  };

  const handleShare = () => {
    // TODO: Implement share functionality
    // Copy URL to clipboard or open share dialog
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    alert('Profile link copied to clipboard!');
    handleMoreClose();
  };

  const handlePrint = () => {
    window.print();
    handleMoreClose();
  };

  /**
   * Navigate to family tree with this person as focus
   * ✅ CRITICAL FIX: Enables direct navigation from profile to tree
   */
  const handleViewInTree = () => {
    navigate(`/tree/${person.id}`);
  };

  // =========================================================================
  // Render
  // =========================================================================

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
        py: { xs: 3, md: 4 }
      }}
    >
      <Container maxWidth="xl">
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', md: 'center' },
            flexDirection: { xs: 'column', md: 'row' },
            gap: 3
          }}
        >
          {/* ===============================================================
              Avatar
              ============================================================ */}
          <Avatar
            src={person.primaryPhotoId}  // TODO Phase 3: Get actual photo URL from PhotoService
            sx={{
              width: { xs: 80, md: 120 },
              height: { xs: 80, md: 120 },
              bgcolor: person.gender?.toLowerCase() === 'male' ? 'primary.main' : 'secondary.main',
              fontSize: { xs: 32, md: 48 },
              fontWeight: 700
            }}
          >
            {initials}
          </Avatar>

          {/* ===============================================================
              Info Section
              ============================================================ */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Name */}
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 700,
                fontSize: { xs: 28, md: 36 },
                mb: 1,
                lineHeight: 1.2
              }}
            >
              {person.firstName}
              {person.middleName && ` ${person.middleName}`}
              {' '}
              {person.lastName}
              {person.suffix && ` ${person.suffix}`}
            </Typography>

            {/* Maiden name */}
            {person.maidenName && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                (née {person.maidenName})
              </Typography>
            )}

            {/* Life info row */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
              {/* Life span & age */}
              <Typography variant="body1" color="text.secondary">
                {lifeSpan}
                {age !== null && ` • Age ${age}`}
              </Typography>

              {/* Status (Living/Deceased) */}
              <Chip
                label={person.isDeceased ? 'Deceased' : 'Living'}
                size="small"
                color={person.isDeceased ? 'default' : 'success'}
                variant={person.isDeceased ? 'outlined' : 'filled'}
              />

              {/* Gender */}
              {person.gender && (
                <Chip
                  icon={person.gender === 'male' ? <MaleIcon /> : <FemaleIcon />}
                  label={person.gender.charAt(0).toUpperCase() + person.gender.slice(1)}
                  size="small"
                  variant="outlined"
                />
              )}

              {/* Location */}
              {person.currentLocation && (
                <Typography variant="body1" color="text.secondary">
                  📍{' '}
                  {person.currentLocation.city}
                  {person.currentLocation.state && `, ${person.currentLocation.state}`}
                  {!person.currentLocation.state && person.currentLocation.country && `, ${person.currentLocation.country}`}
                </Typography>
              )}

              {/* Occupation */}
              {person.occupation && (
                <Typography variant="body1" color="text.secondary">
                  💼 {person.occupation}
                </Typography>
              )}
            </Box>

            {/* Profile completeness bar */}
            <Box sx={{ maxWidth: 400 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Profile Completeness
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => setInfoAnchorEl(e.currentTarget)}
                    sx={{ p: 0.25 }}
                    aria-label="show completeness calculation details"
                  >
                    <InfoIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color={`${completenessColor}.main`}
                >
                  {completeness}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={completeness}
                color={completenessColor}
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>

            {/* User badge - shows if person is a platform member */}
            <Box sx={{ mt: 2 }}>
              <UserBadge personId={person.id} />
            </Box>

            {/* Claim profile button - shows for non-linked users */}
            <Box sx={{ mt: 2 }}>
              <ClaimProfileButton
                person={person}
                onSuccess={() => {
                  console.log('Link request submitted');
                }}
              />
            </Box>

            {/* ✅ Last Updated Metadata (from critical fixes) */}
            {showMetadata && person.updatedAt && (
              <Box sx={{ mt: 1 }}>
                <MetadataDisplay
                  updatedAt={person.updatedAt}
                  updatedByName={person.updatedByName}
                  displayFormat={metadataFormat}
                />
              </Box>
            )}
          </Box>

          {/* ===============================================================
              Actions
              ============================================================ */}
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            {/* ✅ NEW: View in Tree Button (CRITICAL FIX) */}
            <Tooltip title="View in Family Tree">
              <IconButton
                onClick={handleViewInTree}
                sx={{
                  bgcolor: 'secondary.main',
                  color: 'secondary.contrastText',
                  '&:hover': { bgcolor: 'secondary.dark' }
                }}
              >
                <TreeIcon />
              </IconButton>
            </Tooltip>

            {/* Edit Button */}
            <Tooltip title="Edit Profile">
              <IconButton
                onClick={onEdit}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': { bgcolor: 'primary.dark' }
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>

            {/* More Menu */}
            <IconButton onClick={handleMoreClick}>
              <MoreIcon />
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMoreClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right'
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right'
              }}
            >
              <MenuItem onClick={handleShare}>
                <ShareIcon sx={{ mr: 1 }} /> Share Profile
              </MenuItem>
              <MenuItem onClick={handlePrint}>
                <PrintIcon sx={{ mr: 1 }} /> Print Profile
              </MenuItem>
            </Menu>

            {/* Info Popover for Profile Completeness */}
            <Popover
              open={Boolean(infoAnchorEl)}
              anchorEl={infoAnchorEl}
              onClose={() => setInfoAnchorEl(null)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              PaperProps={{
                sx: { maxWidth: 350, maxHeight: 500 }
              }}
            >
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  How Profile Completeness is Calculated
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  The score is based on filled fields weighted by importance. Higher weights contribute more to the score.
                </Typography>

                {loadingConfig ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : config ? (
                  <List dense disablePadding>
                    {['basic', 'dates', 'locations', 'biographical', 'additional'].map((category, catIdx) => {
                      const fields = config.fields.filter(f => f.enabled && f.category === category && isFieldApplicable(f.field));
                      if (fields.length === 0) return null;

                      const categoryLabels: Record<string, string> = {
                        basic: 'Basic Information',
                        dates: 'Dates',
                        locations: 'Locations',
                        biographical: 'Biographical',
                        additional: 'Additional Information',
                      };

                      // Calculate category completion (only applicable fields)
                      const filledCount = fields.filter(f => isFieldFilled(f.field)).length;
                      const totalCount = fields.length;

                      return (
                        <React.Fragment key={category}>
                          {catIdx > 0 && <Divider sx={{ my: 1 }} />}
                          <ListItem disablePadding sx={{ flexDirection: 'column', alignItems: 'flex-start', mb: 0.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 0.5 }}>
                              <Typography variant="caption" fontWeight={600} color="primary">
                                {categoryLabels[category]}
                              </Typography>
                              <Chip
                                label={`${filledCount}/${totalCount}`}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.65rem',
                                  bgcolor: filledCount === totalCount ? 'success.main' : 'warning.main',
                                  color: filledCount === totalCount ? 'success.contrastText' : 'warning.contrastText',
                                  fontWeight: 600,
                                }}
                              />
                            </Box>
                            {fields.map(field => {
                              const isFilled = isFieldFilled(field.field);
                              return (
                                <Box
                                  key={field.field}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                    pl: 1,
                                    py: 0.25
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
                                    {isFilled ? (
                                      <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
                                    ) : (
                                      <CancelIcon sx={{ fontSize: 14, color: 'error.main' }} />
                                    )}
                                    <Typography
                                      variant="caption"
                                      color={isFilled ? 'text.primary' : 'text.secondary'}
                                    >
                                      {field.label}
                                    </Typography>
                                  </Box>
                                  <Typography variant="caption" fontWeight={500} color="text.secondary">
                                    {field.weight} pts
                                  </Typography>
                                </Box>
                              );
                            })}
                          </ListItem>
                        </React.Fragment>
                      );
                    })}
                  </List>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Unable to load configuration
                  </Typography>
                )}
              </Box>
            </Popover>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};
